import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AssessmentResult } from '../utils/scoring';
import { MaturityRadar } from '../components/MaturityRadar';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { DownloadIcon, RefreshCwIcon, CheckCircleIcon, TrendingUpIcon } from 'lucide-react';
export function ReportPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const results = location.state?.results as AssessmentResult | undefined;
  useEffect(() => {
    if (!results) {
      navigate('/');
    }
    window.scrollTo(0, 0);
  }, [results, navigate]);
  if (!results) return null;
  return <div className="min-h-screen bg-slate-50 py-12 px-4 sm:px-6" data-id="element-102">
      <div className="max-w-5xl mx-auto space-y-8" data-id="element-103">
        {/* Header Section */}
        <div className="bg-slate-900 rounded-2xl p-8 md:p-12 text-white shadow-2xl relative overflow-hidden" data-id="element-104">
          <div className="absolute top-0 right-0 w-64 h-64 bg-blue-600 rounded-full blur-3xl opacity-20 -mr-16 -mt-16" data-id="element-105"></div>

          <div className="relative z-10 grid md:grid-cols-2 gap-8 items-center" data-id="element-106">
            <div data-id="element-107">
              <h2 className="text-blue-400 font-medium tracking-wider uppercase text-sm mb-2" data-id="element-108">
                Resultado da Avaliação
              </h2>
              <h1 className="text-4xl md:text-5xl font-bold mb-4" data-id="element-109">
                Nível {results.maturityLevel}
              </h1>
              <p className="text-slate-300 text-lg leading-relaxed max-w-md" data-id="element-110">
                {results.levelDescription}
              </p>
            </div>
            <div className="flex flex-col items-center justify-center bg-white/10 rounded-xl p-6 backdrop-blur-sm border border-white/10" data-id="element-111">
              <span className="text-sm text-slate-300 uppercase tracking-wider mb-1" data-id="element-112">
                Pontuação Geral
              </span>
              <div className="text-6xl font-bold text-white mb-2" data-id="element-113">
                {results.overallScore}{' '}
                <span className="text-2xl text-slate-400 font-normal" data-id="element-114">
                  / 5.0
                </span>
              </div>
              <div className="w-full bg-slate-700 h-2 rounded-full overflow-hidden" data-id="element-115">
                <div className="bg-blue-500 h-full transition-all duration-1000" style={{
                width: `${results.overallScore / 5 * 100}%`
              }} data-id="element-116"></div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-12 gap-8" data-id="element-117">
          {/* Radar Chart Section */}
          <div className="md:col-span-7 space-y-8" data-id="element-118">
            <Card className="p-6 md:p-8" data-id="element-119">
              <h3 className="text-xl font-bold text-slate-900 mb-6 flex items-center" data-id="element-120">
                <TrendingUpIcon className="w-5 h-5 mr-2 text-blue-600" data-id="element-121" />
                Análise por Dimensão
              </h3>
              <MaturityRadar data={results.dimensionScores} data-id="element-122" />

              <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-123">
                {results.dimensionScores.map(dim => <div key={dim.dimension} className="flex justify-between items-center p-3 bg-slate-50 rounded-lg border border-slate-100" data-id="element-124">
                    <span className="font-medium text-slate-700" data-id="element-125">
                      {dim.dimension}
                    </span>
                    <span className={`font-bold ${dim.score >= 4 ? 'text-green-600' : dim.score >= 3 ? 'text-blue-600' : 'text-orange-500'}`} data-id="element-126">
                      {dim.score.toFixed(1)}
                    </span>
                  </div>)}
              </div>
            </Card>
          </div>

          {/* Recommendations Section */}
          <div className="md:col-span-5 space-y-6" data-id="element-127">
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 md:p-8 h-full" data-id="element-128">
              <h3 className="text-xl font-bold text-slate-900 mb-6 flex items-center" data-id="element-129">
                <CheckCircleIcon className="w-5 h-5 mr-2 text-green-600" data-id="element-130" />
                Recomendações Prioritárias
              </h3>
              <div className="space-y-6" data-id="element-131">
                {results.recommendations.map((rec, index) => <div key={index} className="flex gap-4 items-start" data-id="element-132">
                    <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center flex-shrink-0 text-sm font-bold mt-0.5" data-id="element-133">
                      {index + 1}
                    </div>
                    <p className="text-slate-600 leading-relaxed text-sm md:text-base" data-id="element-134">
                      {rec}
                    </p>
                  </div>)}
              </div>

              <div className="mt-8 pt-8 border-t border-slate-100" data-id="element-135">
                <div className="bg-blue-50 rounded-lg p-4 border border-blue-100" data-id="element-136">
                  <h4 className="font-bold text-blue-900 mb-2 text-sm" data-id="element-137">
                    Próximos Passos
                  </h4>
                  <p className="text-blue-700 text-sm" data-id="element-138">
                    Para implementar essas melhorias, considere consultar um
                    especialista em estratégia de PI.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Actions Footer */}
        <div className="flex flex-col sm:flex-row justify-center gap-4 pt-8 pb-12" data-id="element-139">
          <Button size="lg" onClick={() => window.print()} variant="outline" data-id="element-140">
            <DownloadIcon className="w-4 h-4 mr-2" data-id="element-141" />
            Salvar PDF
          </Button>
          <Button size="lg" onClick={() => navigate('/')} data-id="element-142">
            <RefreshCwIcon className="w-4 h-4 mr-2" data-id="element-143" />
            Refazer Avaliação
          </Button>
        </div>
      </div>
    </div>;
}